import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule, NgForm, UntypedFormGroup } from '@angular/forms';
import { SessionContext } from 'src/app/core/services/config/app.settings';
import { HabilitationsService } from 'src/app/data/habilitation/services/habilitations/habilitations.service';
import { Activite } from 'src/app/data/habilitation/models/activite.model';
import { Cahier } from 'src/app/data/habilitation/models/cahier.model';
import { forkJoin } from 'rxjs';
import { ActivitesService } from 'src/app/data/habilitation/services/activites/activites.service';
import { CahiersService } from 'src/app/data/habilitation/services/cahiers/cahiers.service';
import { OperateursService } from 'src/app/data/intervenant/services/operateurs/operateurs.service';
import { OperateurExport } from 'src/app/data/edition/models/operateur-export.model';
import moment from 'moment';
import { OrganismesService } from 'src/app/data/intervenant/services/organismes/organismes.service';
import { Organisme } from 'src/app/data/intervenant/models/organisme.model';
import { ExportToCsv } from 'export-to-csv';
import { HabilitationExport } from 'src/app/data/edition/models/habilitation-export.model';
import { StatutHabilitation } from 'src/app/data/habilitation/models/statut-habilitation.model';
import { Router, ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LoaderService } from '../../../../core/services/loader/loader.service';
import { Table, TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { Select } from 'primeng/select';
import { MultiSelect } from 'primeng/multiselect';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { Column } from '../../../../shared/models/column.model';

@Component({
    selector: 'app-generation-csv',
    templateUrl: './generation-csv.component.html',
    styleUrls: ['./generation-csv.component.scss'],
    imports: [CommonModule, Button, FaIconComponent, TableModule, Select, MultiSelect, FormsModule, TranslateModule],
    standalone: true
})
export class GenerationHabilitationsComponent implements OnInit {
    @ViewChild('form', { static: false }) form: NgForm;
    @ViewChild('columnModal', { static: true }) columnModal: any;
    @ViewChild('dt') dt: Table;
    formGroup: UntypedFormGroup;
    habilitations: HabilitationExport[];
    activites: Activite[];
    cahiers: Cahier[];
    operateurs: OperateurExport[];
    organisme: Organisme;

    page = 1;
    pageSize = 30;

    filters = {
        raisonSociale: '',
        siret: '',
        evv: '',
        adresse: '',
        codePostal: '',
        ville: '',
        telephone: '',
        email: '',
        cahier: '',
        activite: '',
        statut: ''
    };

    typeHabilitations: { name: string; code: string }[] = [];
    typeActivites: { name: string; code: string }[] = [];
    typeCahiers: { name: string; code: string }[] = [];

    columns: Column[] = [
        { field: 'operateur.raisonSociale', label: 'Raison Sociale', visible: true, filter: 'text', width: '20%' },
        { field: 'operateur.siret', label: 'SIRET', visible: true, filter: 'text', width: '20%' },
        { field: 'operateur.cvi', label: 'EVV', visible: false, filter: 'text', width: '20%' },
        { field: 'operateur.adresse', label: 'Adresse', visible: false, filter: 'text', width: '20%' },
        { field: 'operateur.codePostal', label: 'Code Postal', visible: false, filter: 'numeric', width: '20%' },
        { field: 'operateur.ville', label: 'Ville', visible: false, filter: 'text', width: '20%' },
        { field: 'telephone', label: 'Téléphone', visible: false, filter: 'text', width: '20%' },
        { field: 'operateur.email', label: 'Courriel', visible: false, filter: 'text', width: '20%' },
        { field: 'cahier.libelle', label: 'Cahier des charges', visible: true, filter: 'cahier', width: '20%' },
        { field: 'activite.libelle', label: 'Activité', visible: true, filter: 'activite', width: '20%' },
        { field: 'statut.libelle', label: "État d'habilitation", visible: true, filter: 'habilitation', width: '20%' }
    ];

    readonly STORAGE_KEY = 'habilitation_columns_visibility';
    filteredHabilitations: HabilitationExport[] = [];

    private readonly habilitationService = inject(HabilitationsService);
    private readonly activitesService = inject(ActivitesService);
    private readonly cahiersService = inject(CahiersService);
    private readonly loaderService = inject(LoaderService);
    private readonly operateursService = inject(OperateursService);
    private readonly organismeService = inject(OrganismesService);
    private readonly modalService = inject(NgbModal);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);

    ngOnInit() {
        this.loadColumnVisibility();
        this.loadData();
    }

    loadData() {
        forkJoin(
            this.activitesService.getActivites(),
            this.cahiersService.getCahiers(),
            this.habilitationService.getOrganismeHabilitations(SessionContext.get('idOrganisme')),
            this.operateursService.getOperateursByOrganisme(SessionContext.get('idOrganisme')),
            this.organismeService.getOrganisme(SessionContext.get('idOrganisme'))
        )
            .pipe(this.loaderService.operator())
            .subscribe(result => {
                [this.activites, this.cahiers, this.habilitations, this.operateurs, this.organisme] = result;
                this.habilitations.forEach(h => {
                    const operateur = this.operateurs.find(o => o.id === h.idOperateur);
                    if (operateur) {
                        h.operateur = operateur;
                    }
                    h['telephone'] = h.operateur?.telephoneMobile ? h.operateur.telephoneMobile : h.operateur?.telephoneFixe || '';
                });

                this.habilitations.sort((a, b) => {
                    const nameA = a.operateur && a.operateur.raisonSociale ? a.operateur.raisonSociale.toLowerCase() : '';
                    const nameB = b.operateur && b.operateur.raisonSociale ? b.operateur.raisonSociale.toLowerCase() : '';
                    return nameA.localeCompare(nameB);
                });

                this.filteredHabilitations = [...this.habilitations];
                this.typeHabilitations = [...new Set(this.habilitations.map(h => h.statut).sort((a, b) => a.libelle.localeCompare(b.libelle)))].map(rs => ({
                    name: rs.libelle,
                    code: rs.code
                }));
                this.typeActivites = [...new Set(this.habilitations.map(h => h.activite).sort((a, b) => a.libelle.localeCompare(b.libelle)))].map(rs => ({
                    name: rs.libelle,
                    code: rs.code
                }));
                this.typeCahiers = [...new Set(this.habilitations.map(h => h.cahier).sort((a, b) => a.libelle.localeCompare(b.libelle)))].map(rs => ({
                    name: rs.libelle,
                    code: rs.libelle.toUpperCase()
                }));
            });
    }

    toggleColumnVisibility(field: string): void {
        const col = this.columns.find(c => c.field === field);
        if (col) {
            if (!col.visible && this.getVisibleColumnCount() >= 5) {
                return;
            }
            col.visible = !col.visible;
            this.saveColumnVisibility();
        }
    }

    saveColumnVisibility(): void {
        const visibility = this.columns.map(col => ({ field: col.field, visible: col.visible }));
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(visibility));
    }

    loadColumnVisibility(): void {
        const saved = localStorage.getItem(this.STORAGE_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            for (const col of this.columns) {
                const savedCol = parsed.find((c: any) => c.field === col.field);
                if (savedCol) {
                    col.visible = savedCol.visible;
                }
            }
        }
    }

    getVisibleColumnCount(): number {
        return this.columns.filter(col => col.visible).length;
    }

    openColumnModal(): void {
        this.modalService.open(this.columnModal, { centered: true });
    }

    getDateDebut(habilitation: HabilitationExport) {
        let statutHabilitation: StatutHabilitation;
        let retour: string;
        if (habilitation.historique) {
            statutHabilitation = habilitation.historique.find(histo => histo.statut.id === habilitation.statut.id);

            if (statutHabilitation) {
                if (statutHabilitation.dateDebut) {
                    retour = statutHabilitation.dateDebut.format('DD/MM/YYYY');
                } else {
                    retour = '';
                }
            } else {
                retour = '';
            }
        }
        return retour;
    }

    getCommentaire(habilitation: HabilitationExport) {
        let statutHabilitation: StatutHabilitation;
        let retour: string;
        if (habilitation.historique) {
            statutHabilitation = habilitation.historique.find(histo => histo.statut.id === habilitation.statut.id);

            if (statutHabilitation) {
                if (statutHabilitation.commentaire) {
                    retour = statutHabilitation.commentaire;
                } else {
                    retour = '';
                }
            } else {
                retour = '';
            }
        }
        return retour;
    }

    getFieldValue(rowData: HabilitationExport, field: string): string | HabilitationExport {
        if (!field || !rowData) {
            return '';
        }

        const fields = field.split('.');
        let value = rowData;
        for (const f of fields) {
            value = value ? value[f] : '';
        }
        return value;
    }

    exporterHabilitations(): void {
        const data = [];

        const habilitationsAExporter = this.dt.filteredValue ?? this.filteredHabilitations;
        habilitationsAExporter.forEach(habilitation => {
            // this.habilitations.forEach(h => {
            //   h.operateur = this.operateurs.find(operateur => operateur.id === h.idOperateur);
            // });

            const operateur = this.operateurs.find(o => o.id === habilitation.idOperateur);

            if (operateur) {
                habilitation.operateur = operateur;

                data.push({
                    raisonSocialeEtablissement: habilitation.operateur.raisonSociale ? habilitation.operateur.raisonSociale : '',
                    numeroSiret: habilitation.operateur.siret ? habilitation.operateur.siret : '',
                    numeroEvv: habilitation.operateur.cvi ? habilitation.operateur.cvi : '',
                    nomResponsable:
                        !habilitation.operateur.nom && !habilitation.operateur.prenom
                            ? ''
                            : habilitation.operateur.nom && !habilitation.operateur.prenom
                            ? habilitation.operateur.nom
                            : !habilitation.operateur.nom && habilitation.operateur.prenom
                            ? habilitation.operateur.prenom
                            : habilitation.operateur.nom + ' ' + habilitation.operateur.prenom,
                    numeroLibelleVoie: habilitation.operateur.adresse ? habilitation.operateur.adresse : '',
                    codePostal: habilitation.operateur.codePostal ? habilitation.operateur.codePostal : '',
                    commune: habilitation.operateur.ville ? habilitation.operateur.ville : '',
                    numeroTelephone: habilitation.operateur.telephoneMobile
                        ? habilitation.operateur.telephoneMobile
                        : habilitation.operateur.telephoneFixe
                        ? habilitation.operateur.telephoneFixe
                        : '',
                    numeroFax: habilitation.operateur.fax ? habilitation.operateur.fax : '',
                    email: habilitation.operateur.email,
                    cdc: habilitation.cahier.libelle,
                    libelleActiviteOC: habilitation.activite.libelle ? habilitation.activite.libelle : '',
                    datePremiereHabilitation: habilitation.datePremiereHabilitation ? habilitation.datePremiereHabilitation.format('DD/MM/YYYY') : '',
                    etatHabilitation: habilitation.statut ? habilitation.statut.libelle : '',
                    dateDecision: this.getDateDebut(habilitation),
                    observationEventuelle: this.getCommentaire(habilitation)
                });
            }
        });

        const options = {
            filename: 'EDI_INAO_HAB'.concat('_').concat(moment().format('YYYYMMDD')),
            fieldSeparator: ';',
            quoteStrings: '',
            decimalSeparator: '.',
            showLabels: true,
            showTitle: false,
            title: 'EDI_INAO_HAB'.concat('_').concat(moment().format('YYYYMMDD')),
            useTextFile: false,
            useBom: true,
            useKeysAsHeaders: false,
            headers: [
                'Raison sociale établissement',
                'N° SIRET',
                'N° EVV',
                'Nom du responsable',
                'N° et libellé de la voie',
                'Code postal',
                'Commune',
                'N° de téléphone',
                'N° de fax',
                'E-mail',
                'Cahier des charges',
                "Libellé OC de l'activité",
                'Date de 1ère habilitation ou de DI',
                "État d'habilitation",
                'Date de décision (suspension / retrait /...)',
                'Observation éventuelle'
            ]
        };

        const csvExporter = new ExportToCsv(options);

        csvExporter.generateCsv(data);
    }
}
